import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { orderApi } from '../../api/services';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

// --- SubstituteProductModal ------------------------------------------------
export function SubstituteProductModal({ isOpen, onClose, onDone, item }) {
  const [productName, setProductName] = useState('');
  const [indianBrand, setIndianBrand] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  // Kalpana 2026-05-28 (IDM260527126C): qty editable too. Pre-fill with
  // current qty so submitting unchanged = same as today.
  const [quantity, setQuantity] = useState(item?.quantity != null ? String(item.quantity) : '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!productName.trim()) { toast.error('Product name required'); return; }
    const qtyParsed = quantity === '' ? null : Number(quantity);
    if (qtyParsed != null && (!Number.isFinite(qtyParsed) || qtyParsed <= 0)) {
      toast.error('Quantity must be a positive number'); return;
    }
    setSaving(true);
    try {
      await orderApi.substituteProduct(item.id, {
        newProductName: productName,
        newIndianBrand: indianBrand || null,
        newManufacturer: manufacturer || null,
        newQuantity: qtyParsed,
      });
      toast.success('Product substituted');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Substitution failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Substitute product`}>
      <div className="space-y-3">
        <div className="text-xs text-gray-500">
          Current: <span className="font-medium text-gray-700">{item?.product_name}</span>
          {item?.original_product_name && item.original_product_name !== item.product_name && (
            <span className="ml-2 text-gray-400">(original: {item.original_product_name})</span>
          )}
          {item?.quantity != null && (
            <span className="ml-2 text-gray-400">· qty {item.quantity}</span>
          )}
        </div>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">New product name *</span>
          <input value={productName} onChange={(e) => setProductName(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Indian brand (optional)</span>
          <input value={indianBrand} onChange={(e) => setIndianBrand(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Manufacturer (optional)</span>
          <input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Quantity</span>
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)}
                 type="number" min="1" step="1"
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <span className="block text-xs text-gray-500 mt-1">
            Leave unchanged to keep qty {item?.quantity ?? '—'}. Updates the parcel quantity too.
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- PartialShipmentModal -------------------------------------------------
// Per-item shipped/not-shipped picker for post-TN partial shipments.
// Spec: docs/partial-shipment-handling.md (Kalpana 2026-05-26).
// Dispatch User / Admin records exactly what was packed; cancelled items
// route to OPS with reason 'partial_shipment_recorded' and lifecycle
// moves to 'partially_shipped'.
export function PartialShipmentModal({ isOpen, onClose, onDone, order, items = [] }) {
  // selections[itemId] = { shipped: bool, reason: string|null, note: string }
  // useState's initializer only runs on first mount; if `items` is still
  // loading when the modal first mounts, the initial state would stay empty
  // forever (Kalpana 2026-05-26 hit this: counter said "0 shipped · 3 not
  // shipped" because every selections[id] was undefined). Sync via
  // useEffect on items changes, AND make state updates defensive against
  // undefined entries.
  const [selections, setSelections] = useState({});
  const [reasonNote, setReasonNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelections((prev) => {
      const next = { ...prev };
      for (const it of items) {
        if (!next[it.id]) next[it.id] = { shipped: true, reason: null, note: '' };
      }
      // Drop entries for items no longer present.
      for (const id of Object.keys(next)) {
        if (!items.some((it) => it.id === id)) delete next[id];
      }
      return next;
    });
  }, [items]);

  // Sildenafil_above_100mg_blocked intentionally omitted (D9 — vendor-side
  // only, not in Dispatch picker).
  const reasonOptions = [
    { value: 'out_of_stock',              label: 'Out of stock' },
    { value: 'product_not_available',     label: 'Product not available' },
    { value: 'strength_change',           label: 'Strength not available' },
    { value: 'product_brand_changed',     label: 'Brand changed' },
    { value: 'product_not_supported',     label: 'Product no longer needed' },
    { value: 'export_restricted',         label: 'Export restricted' },
    { value: 'short_expiry',              label: 'Short expiry' },
    { value: 'country_service_unavailable', label: 'Country not serviced' },
    { value: 'india_sale_only',           label: 'India sale only' },
    { value: 'other',                     label: 'Other (with note)' },
  ];

  // Defensive: s[itemId] may be undefined transiently if the effect hasn't
  // synced yet — fall back to a default rather than throwing.
  function setShipped(itemId, shipped) {
    setSelections((s) => {
      const cur = s[itemId] || { shipped: true, reason: null, note: '' };
      return { ...s, [itemId]: { ...cur, shipped, reason: shipped ? null : cur.reason } };
    });
  }
  function setReason(itemId, reason) {
    setSelections((s) => {
      const cur = s[itemId] || { shipped: true, reason: null, note: '' };
      return { ...s, [itemId]: { ...cur, reason } };
    });
  }
  function setNote(itemId, note) {
    setSelections((s) => {
      const cur = s[itemId] || { shipped: true, reason: null, note: '' };
      return { ...s, [itemId]: { ...cur, note } };
    });
  }

  const shippedCount   = items.filter((it) => selections[it.id]?.shipped).length;
  const cancelledCount = items.length - shippedCount;
  const cancelledMissingReason = items.some((it) => {
    const s = selections[it.id];
    return s && !s.shipped && !s.reason;
  });

  async function save() {
    if (cancelledCount === 0) {
      toast.error('Mark at least one item as not shipped — otherwise nothing partial to record');
      return;
    }
    if (shippedCount === 0) {
      toast.error('All items cancelled — use the cancellation flow instead of partial shipment');
      return;
    }
    if (cancelledMissingReason) {
      toast.error('Pick a reason for each cancelled item');
      return;
    }
    setSaving(true);
    try {
      await orderApi.recordPartialShipment(order.id, {
        items: items.map((it) => ({
          orderItemId: it.id,
          shipped:     selections[it.id].shipped,
          reason:      selections[it.id].shipped ? null : selections[it.id].reason,
          note:        selections[it.id].note || null,
        })),
        reasonNote: reasonNote.trim() || null,
      });
      toast.success(`Partial shipment recorded — ${shippedCount} shipped, ${cancelledCount} cancelled`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record partial shipment');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update partial shipment">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Record exactly what the vendor packed in the parcel. Items marked <em>Not shipped</em>
          will be cancelled and the order will move to Ops for customer notification.
        </p>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Reason / note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const s = selections[it.id] || { shipped: true, reason: null, note: '' };
                return (
                  <tr key={it.id} className={`border-t border-gray-100 ${s.shipped ? '' : 'bg-amber-50/40'}`}>
                    <td className="px-3 py-2">
                      <div className={s.shipped ? '' : 'line-through text-gray-500'}>{it.product_name}</div>
                      <div className="text-xs text-gray-500">{it.indian_brand || '—'} · qty {it.quantity}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2 text-xs">
                        <label className="flex items-center gap-1">
                          <input type="radio" name={`ship-${it.id}`} checked={s.shipped} onChange={() => setShipped(it.id, true)} />
                          Shipped
                        </label>
                        <label className="flex items-center gap-1">
                          <input type="radio" name={`ship-${it.id}`} checked={!s.shipped} onChange={() => setShipped(it.id, false)} />
                          Not shipped
                        </label>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {!s.shipped && (
                        <div className="space-y-1.5">
                          <select value={s.reason || ''} onChange={(e) => setReason(it.id, e.target.value || null)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs">
                            <option value="">Pick a reason *</option>
                            {reasonOptions.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                          <input value={s.note} onChange={(e) => setNote(it.id, e.target.value)}
                                 placeholder="Optional note from vendor"
                                 className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Overall note (optional)</span>
          <textarea value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="text-xs text-gray-500">
          {shippedCount} shipped · {cancelledCount} not shipped
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Record partial shipment
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- RecordItemRefundModal ------------------------------------------------
// OPS records the refund amount issued for a single cancelled item (D7).
export function RecordItemRefundModal({ isOpen, onClose, onDone, item }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    const n = Number(amount);
    if (!Number.isFinite(n) || n < 0) { toast.error('Enter a valid refund amount (>= 0)'); return; }
    setSaving(true);
    try {
      await orderApi.recordItemRefund(item.id, { amount: n, note: note.trim() || null });
      toast.success('Refund recorded');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record refund');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record refund">
      <div className="space-y-3">
        <div className="text-sm">
          <div className="text-gray-500">Cancelled item</div>
          <div className="font-medium text-gray-900">{item?.product_name}</div>
          <div className="text-xs text-gray-500">{item?.indian_brand || '—'} · qty {item?.quantity}</div>
          {item?.cancelled_reason && (
            <div className="text-xs text-amber-700 mt-1">Cancellation reason: {item.cancelled_reason}</div>
          )}
        </div>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Refund amount *</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="0.01"
                 placeholder="0.00"
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    placeholder="e.g. refunded to customer's card on 2026-05-26"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Record refund
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- ClarifyVirtualProductModal -------------------------------------------
export function ClarifyVirtualProductModal({ isOpen, onClose, onDone, order, item }) {
  const [productName, setProductName] = useState('');
  const [indianBrand, setIndianBrand] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  // Kalpana 2026-05-28: virtual products often carry a placeholder qty
  // that needs correcting once Ops confirms the real product.
  const [quantity, setQuantity] = useState(item?.quantity != null ? String(item.quantity) : '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!productName.trim()) { toast.error('Real product name required'); return; }
    const qtyParsed = quantity === '' ? null : Number(quantity);
    if (qtyParsed != null && (!Number.isFinite(qtyParsed) || qtyParsed <= 0)) {
      toast.error('Quantity must be a positive number'); return;
    }
    setSaving(true);
    try {
      await orderApi.clarifyVirtualProduct(order.id, {
        orderItemId: item.id,
        realProductName: productName,
        realIndianBrand: indianBrand || null,
        realManufacturer: manufacturer || null,
        newQuantity: qtyParsed,
      });
      toast.success('Virtual product clarified — order moved to intake');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Clarification failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Clarify virtual product">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Confirm the real product with the customer, then record the actual name/brand/manufacturer.
          This moves the order out of <em>data_incomplete</em> back to the normal flow.
        </p>
        <div className="text-xs text-gray-500">
          Generic name from MBO: <span className="font-medium text-gray-700">{item?.generic_name || '(none)'}</span>
          {item?.quantity != null && (
            <span className="ml-2 text-gray-400">· qty {item.quantity}</span>
          )}
        </div>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Real product name *</span>
          <input value={productName} onChange={(e) => setProductName(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Brand</span>
          <input value={indianBrand} onChange={(e) => setIndianBrand(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</span>
          <input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Quantity</span>
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)}
                 type="number" min="1" step="1"
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <span className="block text-xs text-gray-500 mt-1">
            Leave unchanged to keep qty {item?.quantity ?? '—'}. Updates the parcel quantity too.
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Clarify
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- LinkReshipModal ------------------------------------------------------
// Two-step flow:
//  1. Admin enters Parent Order ID, clicks Link. Backend looks up the parent
//     in DMS and inherits its vendor.
//  2. If the parent isn't in DMS (PARENT_NOT_FOUND), modal switches to a
//     fallback view: admin picks a vendor manually and re-submits. Parent
//     external id is stored for audit reference.
export function LinkReshipModal({ isOpen, onClose, onDone, order, vendors = [] }) {
  const [parentId, setParentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [fallback, setFallback] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [oldTracking, setOldTracking] = useState('');
  const [reshipReason, setReshipReason] = useState('');

  // Reset when modal re-opens
  function close() {
    setFallback(false); setVendorId(''); setParentId('');
    setOldTracking(''); setReshipReason('');
    onClose();
  }

  async function save() {
    if (!parentId.trim()) { toast.error('Parent Order ID is required'); return; }
    setSaving(true);
    try {
      if (fallback) {
        if (!vendorId) { toast.error('Pick a vendor to proceed'); setSaving(false); return; }
        const res = await orderApi.linkReshipExternal(order.id, {
          parentExternalId: parentId.trim(),
          vendorId,
          originalTrackingNumber: oldTracking.trim() || null,
          reshipReason: reshipReason || null,
        });
        toast.success('Linked with external parent — vendor set manually');
        onDone?.(res.data);
        close();
        return;
      }

      const res = await orderApi.linkReship(order.id, { parentExternalId: parentId.trim() });
      toast.success(`Linked to parent order — vendor inherited`);
      onDone?.(res.data);
      close();
    } catch (err) {
      const c = err.response?.data?.code;
      if (c === 'PARENT_NOT_FOUND') {
        // Flip into fallback mode instead of just erroring
        setFallback(true);
        toast('Parent not in DMS — pick a vendor manually to proceed', { icon: 'ℹ️' });
      } else {
        const map = {
          PARENT_VENDOR_MISSING: 'Parent has no vendor allocated yet',
          RESHIP_CHAIN_EXCEEDED: 'Max 2 reships per original — escalate to Ops',
          NOT_A_RESHIP: 'Order is not a reship',
          INVALID_STAGE: 'Order already linked or in wrong state',
          VENDOR_NOT_FOUND: 'Selected vendor not found',
          VENDOR_INACTIVE: 'Selected vendor is deactivated',
        };
        toast.error(map[c] || err.response?.data?.error || 'Link failed');
      }
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={close} title="Link reship to original order">
      <div className="space-y-3">
        {!fallback ? (
          <p className="text-sm text-gray-600">
            Look up the Parent Order ID in the MBO portal (search by this reship's Order ID,
            then read the Parent Order ID), and paste it here. The reship will inherit the
            original order's vendor.
          </p>
        ) : (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <strong>Parent not in DMS.</strong> That's OK — we can still link the reship.
            Keep the Parent Order ID on file for reference, and pick a vendor manually
            below. This covers parents that shipped before v2 or were fulfilled via
            another system.
          </div>
        )}
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Parent Order ID</span>
          <input
            value={parentId} onChange={(e) => setParentId(e.target.value)}
            placeholder="e.g. IDM260408082C"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
          />
        </label>
        {fallback && (
          <>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Vendor *</span>
              <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">— select vendor —</option>
                {vendors.filter((v) => v.is_active !== false).map((v) => (
                  <option key={v.id} value={v.id}>{v.name}{v.code ? ` (${v.code})` : ''}</option>
                ))}
              </select>
            </label>
            {/* Optional but recommended: so the -RESHIP XLSX 'Old Tracking'
                and 'Reship Reason' columns aren't blank when the parent
                isn't in our DMS. */}
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">
                Original (old) tracking number
                <span className="text-xs text-gray-500 ml-1 font-normal">— optional, shown on the vendor file</span>
              </span>
              <input value={oldTracking} onChange={(e) => setOldTracking(e.target.value)}
                     placeholder="e.g. EE123456789IN"
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">
                Reship reason
                <span className="text-xs text-gray-500 ml-1 font-normal">— leave blank to keep existing</span>
              </span>
              <select value={reshipReason} onChange={(e) => setReshipReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">— keep existing reason —</option>
                {/* Must match the reship_reason ENUM in schema.sql §Types */}
                <option value="lost">Lost / returned / not delivered</option>
                <option value="damaged">Damaged in transit</option>
                <option value="customer_feedback">Customer feedback — product didn't work</option>
              </select>
            </label>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          {fallback && (
            <button onClick={() => setFallback(false)}
                    className="mr-auto text-xs text-blue-600 hover:underline">
              ← try looking up the parent again
            </button>
          )}
          <button onClick={close} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {fallback ? 'Link with manual vendor' : 'Link'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- RevisedTrackingModal — B7 post-completion TN edit --------------------
export function RevisedTrackingModal({ isOpen, onClose, onDone, shipment }) {
  const [tn, setTn] = useState('');
  const [multiOrder, setMultiOrder] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!tn.trim()) { toast.error('New tracking number required'); return; }
    setSaving(true);
    try {
      const { trackingApi } = await import('../../api/services');
      const res = await trackingApi.updateRevised(shipment.id, {
        newTrackingNumber: tn.trim(),
        isMultiOrderShipment: multiOrder,
      });
      const flags = [];
      if (res.data.invalid_tracking) flags.push('flagged as invalid format');
      if (res.data.duplicate_tracking) flags.push('flagged as duplicate');
      toast.success(['Tracking revised', ...flags].join(' · '));
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Revision failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Revise tracking number">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Use this after the order is completed, when the vendor sends a corrected
          tracking number. The order's lifecycle doesn't change; an audit entry is
          written and Ops is notified if duplicate / invalid.
        </p>
        <div className="text-xs text-gray-500">
          Current: <span className="font-medium text-gray-700">{shipment?.tracking_number || '(none)'}</span>
        </div>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">New tracking number *</span>
          <input value={tn} onChange={(e) => setTn(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={multiOrder} onChange={(e) => setMultiOrder(e.target.checked)} />
          This TN intentionally spans multiple orders (bulk shipment)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Revise
          </button>
        </div>
      </div>
    </Modal>
  );
}

// --- SendBackModal --------------------------------------------------------
export function SendBackModal({ isOpen, onClose, onDone, order }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!note.trim()) { toast.error('Please explain what needs correction'); return; }
    setSaving(true);
    try {
      await orderApi.sendBack(order.id, {
        toUserId: order.current_owner_user_id || null,
        reasonNote: note,
      });
      toast.success('Sent back for correction');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Send back failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send back for correction">
      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">What needs correction? *</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Send back
          </button>
        </div>
      </div>
    </Modal>
  );
}
